import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router-dom'

import { Box } from '@mui/material'

import { GRSubTabNavigation } from '../../../components/GRTabNavigation/GRTabNavigation'
import GameNotAnalyzedCard from '../../../components/GameNotAnalyzedCard/GameNotAnalyzedCard'
import GameRemovedFromAppStoreWarning from '../../../components/GameRemovedFromAppStoreWarning/GameRemovedFromAppStoreWarning'
import { LockedFeature } from '../../../components/LockedFeature/LockedFeature'
import analyticsService from '../../../services/AnalyticsService'
import { LockedFeatureId } from '../../../types/LockedFeature'
import { useRoleCheck, useLiveEventsTrackerAccessCheck } from '../../account/hooks/roleHooks'
import { useCurrentUserLanguage } from '../../account/hooks/useCurrentUserLanguage'
import { RoleEnum } from '../../account/types/RoleEnum'
import { Analysis } from '../../analysis/types/Analysis'
import { Game } from '../../game'
import { UnlockGame } from '../../game/components/UnlockGame/UnlockGame'
import { useGameFollow } from '../../game/hooks/gameHooks'
import { GameArchiveStatus } from '../../game/types/Game'
import { useIsGameOpenForEveryone } from '../../settings'
import ImplementationExamplesSubtab from './ImplementationExamplesSubtab'
import LiveEventExamplesSubtab from './LiveEventExamplesSubtab'

type GameImplementationsProps = {
  game: Game
  analysis?: Analysis
  isGameUnlocked?: boolean
  isGameAnalyzed?: boolean
  hideListScreenshots?: boolean
  gameArchiveStatus?: GameArchiveStatus
}
const GameImplementations: React.FC<GameImplementationsProps> = ({
  game,
  isGameUnlocked,
  isGameAnalyzed,
  analysis,
  hideListScreenshots,
  gameArchiveStatus,
}) => {
  useEffect(() => {
    analyticsService.trackEvent('Visited Game Overview: Implementations', {
      data: {
        gameId: game.id,
        gameName: game.resolvedName,
        analysisId: analysis?.id,
      },
    })
  }, [analysis?.id, game.id, game.resolvedName])
  const { t } = useTranslation()
  const userLanguage = useCurrentUserLanguage()
  const isGameOpenForEveryone = useIsGameOpenForEveryone(game)
  const allGamesUnlocked = useRoleCheck(RoleEnum.all_games_unlocked)
  const { isFollowed: isGameFollowed } = useGameFollow(game)
  const isGameImplementationsFeatureUnlocked = useRoleCheck(RoleEnum.feature_implementation_examples_unlimited)
  const displayUnlockGameCard = !(allGamesUnlocked || isGameFollowed || isGameOpenForEveryone) && isGameImplementationsFeatureUnlocked
  const displayImplementations = isGameOpenForEveryone || isGameImplementationsFeatureUnlocked
  const hasAccessToLiveEventsTracker = useLiveEventsTrackerAccessCheck()
  const params = useParams<{ id: string; subpage: string; subtab: string }>()

  // Check if game is tracked for live events
  const isGameTracked = game.isTracked === true

  // Subtab definitions
  const subTabs = useMemo(() => {
    return [
      {
        id: 'features',
        name: t('implementations:tab_feature_examples'),
        disabled: false,
      },
      {
        id: 'live-events',
        name: t('implementations:tab_live_event_examples'),
        disabled: !isGameTracked,
        disabledTooltip: t('implementations:game_not_tracked_tooltip'),
      },
    ]
  }, [isGameTracked, t])

  const defaultSubTab = subTabs[0]
  const [selectedSubTab, setSelectedSubTab] = useState(subTabs.find((subTab) => subTab.id === params.subtab) || defaultSubTab)

  if (params.subpage === 'implementations' && !params.subtab) {
    return <Navigate replace to={`/game/${game.id}/implementations/features`} />
  } else {
    return (
      <Box>
        {!isGameAnalyzed && gameArchiveStatus === GameArchiveStatus.archived && <GameRemovedFromAppStoreWarning />}
        <>
          <Box sx={{ mb: 3 }}>
            <GRSubTabNavigation
              tabs={subTabs}
              selectedTab={subTabs.find((tab) => tab.id === selectedSubTab.id)?.id}
              pagePrefix={`/game/${game.id}/implementations`}
              onChange={(event, newValue) => setSelectedSubTab(subTabs.find((subTab) => subTab.id === newValue) || defaultSubTab)}
            />
          </Box>

          {selectedSubTab.id === 'features' && (
            <>
              {!isGameAnalyzed && gameArchiveStatus !== GameArchiveStatus.archived && <GameNotAnalyzedCard game={game} />}
              {isGameAnalyzed && (
                <>
                  {!displayImplementations ? (
                    <LockedFeature.Card lockedFeatureId={LockedFeatureId.GameImplementations} />
                  ) : displayUnlockGameCard ? (
                    <UnlockGame.Card game={game} />
                  ) : (
                    <ImplementationExamplesSubtab game={game} />
                  )}
                </>
              )}
            </>
          )}

          {selectedSubTab.id === 'live-events' && (
            <LiveEventExamplesSubtab
              game={game}
              userLanguage={userLanguage}
              hasAccessToLiveEventsTracker={hasAccessToLiveEventsTracker}
              isGameTracked={isGameTracked}
            />
          )}
        </>
      </Box>
    )
  }
}

export default GameImplementations
